import { BrandLoader } from '@/components/common/BrandLoader'
import { Check, X } from 'lucide-react'
import { cn, Progress, Skeleton } from '@teach/ui'
import type { ReactNode } from 'react'
import type { Lang } from '@/types/lang'
import { runSummaryText, type TranslationRunView } from '@/features/ai/lib/translationRun'
import { failedReasonText } from '@/features/ai/lib/failedReasonText'
import { MenuButton } from './MenuButton'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type RunningView = Extract<TranslationRunView, { kind: 'running' }>
type FinishedView = Extract<TranslationRunView, { kind: 'finished' }>

interface RunningProps {
  /** `unknown` — запись реестра есть, ответа опроса ещё нет. */
  view: RunningView | { kind: 'unknown' }
  targetLang: Lang
  onAskCancel: () => void
  isCancelling: boolean
  cancelFailure: string | null
}

/** Одна плашка на ход и итог прогона: элемент остаётся тем же, поэтому цвет перетекает в зелёный. */
export const RunFrame = ({ success, children }: { success: boolean; children: ReactNode }) => (
  <div
    className={cn(
      // Те же поля, что у панели запуска: плашка по ширине кнопки «Перевести».
      'mx-2 mt-2 rounded-xl border p-2.5 transition-colors duration-700',
      success ? 'border-success/50 bg-success/15' : 'border-border bg-muted/30',
    )}
  >
    {children}
  </div>
)

/**
 * Идущий прогон в меню языка: общая полоса на текст и картинки (§6.4) и
 * отмена. Сама отмена спрашивает подтверждение — диалог живёт вне меню.
 * Вид считает `translationRunView` — здесь только отрисовка.
 */
export const TranslationRunning = ({
  view,
  targetLang,
  onAskCancel,
  isCancelling,
  cancelFailure,
}: RunningProps) => {
  const m = useMessages(messages)
  if (view.kind === 'unknown') {
    // Запись реестра могла пролежать в persist: без ответа опроса числа — ложь.
    // Системный статус автору не нужен: скелетон держит место под полосой прогресса.
    return (
      <div data-testid="run-checking" aria-hidden className="m-2 space-y-2">
        <Skeleton className="h-3.5 w-full rounded-full" />
        <Skeleton className="h-4 w-40 rounded-full" />
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {/* `aria-valuenow` ставит сам Radix из `value`. */}
        <Progress
          value={view.percent}
          aria-label={m.progress}
          className="h-2.5 flex-1 bg-muted [&>div]:animate-shimmer [&>div]:bg-[linear-gradient(90deg,hsl(var(--brand))_30%,hsl(var(--brand)/0.55)_50%,hsl(var(--brand))_70%)] [&>div]:bg-[length:200%_100%] motion-reduce:[&>div]:animate-none"
        />
        <span className="w-8 shrink-0 text-right text-[11px] font-semibold tabular-nums text-muted-foreground">
          {view.percent}%
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-foreground">
        <BrandLoader className="size-4 shrink-0" />
        <span className="truncate">{m.translatingTo(targetLang)}</span>
      </div>
      <MenuButton disabled={isCancelling} onSelect={onAskCancel}>
        {m.cancelTranslation}
      </MenuButton>
      {cancelFailure ? <p className="text-xs text-destructive">{cancelFailure}</p> : null}
    </div>
  )
}

/** Итог прогона. Отмена — нейтральный итог, не ошибка: автор сам её выбрал.
 *  У прервавшегося — русская причина под строкой итога. Крестик меню не
 *  закрывает: под итогом та же панель запуска. */
export const TranslationSummary = ({
  view,
  onDismiss,
}: {
  view: FinishedView
  onDismiss: () => void
}) => {
  const m = useMessages(messages)
  const reason =
    view.status === 'failed' ? failedReasonText(view.job.error_code, view.job.error_message) : null
  return (
    <div className="space-y-1 text-xs duration-500 animate-in fade-in">
      <div className="flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-2 text-foreground">
          {view.status === 'success' ? (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground duration-500 animate-in zoom-in-50">
              <Check aria-hidden className="size-3.5" />
            </span>
          ) : null}
          {view.status === 'success'
            ? m.translationDone
            : runSummaryText(view.status, view.textDone)}
        </p>
        <MenuButton
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-label={m.hideSummary}
          onSelect={onDismiss}
        >
          <X className="size-3.5" />
        </MenuButton>
      </div>
      {reason ? <p className="text-muted-foreground">{reason}</p> : null}
    </div>
  )
}
